export type PaginationItem = {
  type: 'nearby-page' | 'prev' | 'next' | 'first' | 'last' | 'midrange';
  pageNumber: number;
  isCurrent?: boolean;
  indicateOmittedPages?: 'before' | 'after';
  disabled?: boolean;
  omitOnSmallDevices?: boolean;
};

type Options = { alwaysShowStepButtons?: boolean };

function notNull<T>(item: T | null): item is T {
  return item !== null;
}

function getPrevPageItem(
  currentPage: number,
  alwaysShowStepButtons?: boolean
): PaginationItem | null {
  if (currentPage === 1 && !alwaysShowStepButtons) {
    return null;
  }

  return {
    type: 'prev',
    pageNumber: currentPage - 1,
    disabled: currentPage === 1,
  };
}

function getFirstPageItem(nearbyPages: number[]): PaginationItem | null {
  const firstNearbyPage = Math.min(...nearbyPages);
  if (firstNearbyPage === 1) {
    return null;
  }

  return {
    type: 'first',
    pageNumber: 1,
    indicateOmittedPages: firstNearbyPage > 2 ? 'after' : undefined,
  };
}

function getNearbyPages(
  currentPage: number,
  totalPages: number
): PaginationItem[] {
  const from = Math.max(1, Math.min(totalPages - 4, currentPage - 2));
  const to = Math.min(totalPages, Math.max(5, currentPage + 2));

  const pages = [];
  for (let i = from; i <= to; i += 1) {
    const page: PaginationItem = {
      type: 'nearby-page' as const,
      pageNumber: i,
      isCurrent: currentPage === i,
    };

    if ([from, to].includes(i)) {
      page.omitOnSmallDevices = true;
    }

    pages.push(page);
  }
  return pages;
}

const getMidrangePage = (
  position: 'before' | 'after',
  nearbyPages: number[],
  totalPages: number
): PaginationItem | null => {
  const threshold = 5;
  const firstNearbyPage = Math.min(...nearbyPages);
  const lastNearByPage = Math.max(...nearbyPages);

  switch (position) {
    case 'before':
      if (firstNearbyPage < threshold) {
        return null;
      }

      return {
        type: 'midrange',
        pageNumber: Math.ceil(firstNearbyPage / 2),
        indicateOmittedPages: 'after',
      };
    case 'after':
      if (lastNearByPage + threshold > totalPages) {
        return null;
      }

      return {
        type: 'midrange',
        pageNumber:
          Math.floor((totalPages - lastNearByPage) / 2) + lastNearByPage,
        indicateOmittedPages: 'before',
      };
    default:
      return null;
  }
};

function getLastPageItem(
  nearbyPages: number[],
  totalPages: number
): PaginationItem | null {
  const lastNearbyPage = Math.max(...nearbyPages);
  if (lastNearbyPage === totalPages) {
    return null;
  }

  return {
    type: 'last',
    pageNumber: totalPages,
    indicateOmittedPages:
      lastNearbyPage < totalPages - 1 ? 'before' : undefined,
  };
}

function getNextPageItem(
  currentPage: number,
  totalPages: number,
  alwaysShowStepButtons?: boolean
): PaginationItem | null {
  if (currentPage === totalPages && !alwaysShowStepButtons) {
    return null;
  }

  return {
    type: 'next',
    pageNumber: currentPage + 1,
    disabled: currentPage === totalPages,
  };
}

export function getPaginationItems(
  currentPage: number,
  totalPages: number,
  { alwaysShowStepButtons = false }: Options = {}
): PaginationItem[] {
  if (totalPages <= 1) {
    return [];
  }

  const clampedCurrentPage = Math.max(1, Math.min(totalPages, currentPage));
  const nearbyPages = getNearbyPages(clampedCurrentPage, totalPages);
  const nearbyPageNrs = nearbyPages.map(page => page.pageNumber);

  return [
    getPrevPageItem(clampedCurrentPage, alwaysShowStepButtons),
    getFirstPageItem(nearbyPageNrs),
    getMidrangePage('before', nearbyPageNrs, totalPages),
    ...nearbyPages,
    getMidrangePage('after', nearbyPageNrs, totalPages),
    getLastPageItem(nearbyPageNrs, totalPages),
    getNextPageItem(clampedCurrentPage, totalPages, alwaysShowStepButtons),
  ].filter(notNull);
}
